import { Injectable } from "@angular/core";

import { EmailAddress } from "../models/domains/email/email-address";
import { RemoteGatewayFactory } from "../gateways/remote-gateway-factory";
import { ForgotPasswordUrl } from "../models/domains/urls/forgot-password.url";
import { environment } from "src/environments/environment";
import { UserCredentials } from "../models/domains/credentials/user-credentials.model";
import { UserCredentialsActivationParser } from "./parsers/user-credentials-activation.parser";
import { PageableResult } from "../models/domains/paginator/pageable-result";
import { PagingRequest } from "../models/domains/paginator/paging-request";
import { BackendUrl } from "../models/domains/urls/back-end.url";
import { SearchUserParser } from "./parsers/pbm/user/search-user.parser";
import { SearchUserByUserKeyParser } from "./parsers/pbm/user/search-user-by-user-key.parser";
import { RequestUserRolesParser } from "./parsers/pbm/user/request-user-roles.parser";
import { UserRole } from "../models/domains/pbm/user/user-role";
import { User } from "../models/domains/pbm/user/user";
import { ResponseErrorParser } from "./parsers/pbm/response-error.parser";

@Injectable()
export class UserRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private userCredentialActivationParser: UserCredentialsActivationParser,
    private searchUserParser: SearchUserParser,
    private searchUserByUserKeyParser: SearchUserByUserKeyParser,
    private requestUserRolesParser: RequestUserRolesParser,
    private responseErrorParser: ResponseErrorParser,
  ) {}

  async sendEmailForgotPassword(email: EmailAddress): Promise<any> {
    const body = {
      Email: email.getAddress(),
      Url: environment.forgotPasswordConfirmationUrl,
      ApplicationId: environment.authClientId,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new ForgotPasswordUrl("/api/Account/ForgotPasswordWithConfirmationUrl"), body, headers);
      return Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    const body = {
      currentPassword,
      newPassword,
      confirmedPassword: confirmPassword,
    };
    try {
      const gateway = this.remoteGatewayFactory.createAuthRemoteGateway();
      await gateway.post("/api/account/changepassword", body);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async changePasswordFirstAccess(password: string, userCredentials: UserCredentials): Promise<boolean> {
    let passwordChanged = false;
    const headers = {
      "Content-Type": "application/json",
    };
    const body = {
      UserId: userCredentials.userId,
      Token: userCredentials.token,
      NewPassword: password,
      PasswordConfirmation: password,
    };
    const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
    try {
      const data = await remoteGateway.postWithHeaders(new ForgotPasswordUrl("/api/Account/changepasswordfirstaccess"), body, headers).then((value) => {
        return (passwordChanged = true);
      });
    } catch (error) {
      return await Promise.reject(error);
    }
    return passwordChanged;
  }

  async activateUser(userCredentials: UserCredentials): Promise<UserCredentials> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.get(new ForgotPasswordUrl("/api/Account/RegisterConfirmation?param=" + userCredentials.token));
      return this.userCredentialActivationParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  async searchUser(pagingRequest: PagingRequest, userFiscalNumber: string): Promise<PageableResult> {
    const body = {
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
      FilterText: pagingRequest.getFilterText(),
    };

    const headers = {
      "Content-Type": "application/json",
      //'X-CompanyFiscalNumberRoot-Filter': userFiscalNumber
    };

    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().postWithHeaders(new BackendUrl("/api/user/users"), body, headers);
      return this.searchUserParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  async requestUserRoles(): Promise<UserRole[]> {
    const headers = { "Content-Type": "application/json" };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/user/userRoles"), headers);
      return this.requestUserRolesParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  async addUser(user: User, password: string): Promise<any> {
    const userRoles = user.getUserRoleList().map((userRole) => {
      return {
        UserRoleKey: {
          Value: userRole.getUserRoleKey(),
        },
        Name: userRole.getName(),
        UserPermissions: userRole.getUserPermissionList().map((permission) => {
          return {
            UserPermissionKey: {
              Value: permission.getUserPermissionKey(),
            },
            Code: permission.getCode(),
            Name: permission.getName(),
            UserRoleType: permission.getUserRoleType(),
          };
        }),
        UserRoleType: userRole.getUserRoleType(),
      };
    });

    const body = JSON.stringify({
      Name: user.getName(),
      Email: user.getEmail(),
      CorrelatedId:user.correlatedId,
      Password: password,
      CompanyInfo: user.getUserCompanyInfo(),
      UserRoles: userRoles,
      AllowedCompanies: user.getAllowedCompanies(),
    });

    const headers = { "Content-Type": "application/json" };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/user");
      const payload: any = await remoteGateway.postWithHeaders(backEndUrl, body, headers);
      return payload.Value;
    } catch (error) {
      const responseError = this.responseErrorParser.parse(error);

      return await Promise.reject(responseError);
    }
  }

  async updateUser(user: User, newPassword: string, confirmNewPassord: string): Promise<any> {
    const userRoles = user.getUserRoleList().map((userRole) => {
      return {
        UserRoleKey: {
          Value: userRole.getUserRoleKey(),
        },
        Name: userRole.getName(),
        UserPermissions: userRole.getUserPermissionList().map((permission) => {
          return {
            UserPermissionKey: {
              Value: permission.getUserPermissionKey(),
            },
            Code: permission.getCode(),
            Name: permission.getName(),
            UserRoleType: permission.getUserRoleType(),
          };
        }),
        UserRoleType: userRole.getUserRoleType(),
      };
    });

    const body = JSON.stringify({
      UserKey: {
        Value: user.getUserKey(),
      },
      Name: user.getName(),
      Email: user.getEmail(),
      NewPassword: newPassword,
      CorrelatedId:user.correlatedId,
      ConfirmNewPassword: confirmNewPassord,
      Active: user.isActivate(),
      CompanyInfo: user.getUserCompanyInfo(),
      UserRoles: userRoles,
      AllowedCompanies: user.getAllowedCompanies(),
    });

    const headers = { "Content-Type": "application/json" };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/user");
      return await remoteGateway.putWithHeaders(backEndUrl, body, headers);
    } catch (error) {
      const responseError = this.responseErrorParser.parse(error);
      return await Promise.reject(responseError);
    }
  }

  async searchUserByUserKey(userKey: string, userFiscalNumber: string): Promise<User> {
    const headers = {
      "Content-Type": "application/json",
      //'X-CompanyFiscalNumberRoot-Filter': userFiscalNumber
    };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/user/" + userKey);
      const data = await remoteGateway.getWithHeaders(backEndUrl, headers);
      return this.searchUserByUserKeyParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }
}
